import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Wallet, Loader2, TrendingUp, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatCard } from "@/components/DashboardShell";
import api, { formatApiError } from "@/lib/api";

export function PartnerWallet() {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/partner/wallet"); setW(data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!w) return <div className="py-8 grid place-items-center"><Loader2 className="w-6 h-6 text-[#E53E3E] animate-spin" /></div>;

  const sym = (c) => w.symbols?.[c] || (c === "USD" ? "$" : "");
  const fmt = (n, c) => `${sym(c)}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const currencies = Array.from(new Set([...Object.keys(w.balances || {}), ...Object.keys(w.estimated_totals || {})]));
  if (currencies.length === 0) currencies.push("USD");

  const withdraw = async (cur) => {
    setBusy(cur);
    try {
      const { data } = await api.post("/partner/wallet/withdraw", { currency: cur });
      toast.success(`Withdrawal of ${fmt(data.amount, cur)} requested.${data.awaiting_provider ? " Awaiting payout provider setup." : ""}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div className="mt-8" data-testid="partner-wallet">
      <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4 flex items-center gap-2"><Wallet className="w-6 h-6 text-[#E53E3E]" /> Earnings & Wallet</h2>

      {currencies.map((cur) => {
        const b = (w.balances || {})[cur] || { pending: 0, available: 0, paid: 0, total_earned: 0 };
        const threshold = (w.thresholds || {})[cur] ?? 0;
        const est = (w.estimated_totals || {})[cur] || 0;
        return (
          <div key={cur} className="mb-6" data-testid={`wallet-currency-${cur}`}>
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">{cur} Balance</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Estimated This Month" value={fmt(est, cur)} hint={`${w.paying_members} paying member(s)`} />
              <StatCard label="Pending" value={fmt(b.pending, cur)} hint="Settles after period" />
              <StatCard label="Available" value={fmt(b.available, cur)} hint={`Min ${fmt(threshold, cur)} to withdraw`} />
              <StatCard label="Total Earned" value={fmt(b.total_earned, cur)} />
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button onClick={() => withdraw(cur)} disabled={busy === cur || b.available < threshold} className="bg-[#E53E3E] hover:bg-[#C53030] rounded-full disabled:opacity-50" data-testid={`withdraw-button-${cur}`}>
                {busy === cur ? <Loader2 className="w-4 h-4 animate-spin" /> : `Withdraw ${cur}`}
              </Button>
              {!w.paystack_configured && <span className="text-[11px] text-amber-400">Payouts awaiting Paystack provider setup.</span>}
            </div>
          </div>
        );
      })}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6">
          <h3 className="font-display text-lg font-bold uppercase tracking-tight flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#E53E3E]" /> Performance Breakdown</h3>
          {(!w.performance_items || w.performance_items.length === 0) ? <p className="mt-3 text-sm text-zinc-500">No paying members this period.</p> :
            w.performance_items.map((it) => (
              <div key={it.member_id} className="mt-4 rounded-xl border border-zinc-800 p-4" data-testid={`perf-${it.member_id}`}>
                <div className="flex items-center justify-between"><p className="font-medium">{it.member_name}</p><Badge className="bg-[#E53E3E]/15 text-[#E53E3E]">{it.score}% · {fmt(it.estimated, it.currency)}</Badge></div>
                <div className="mt-2 grid grid-cols-2 gap-1 text-xs text-zinc-500">
                  <span>Verify: {(it.breakdown.verify * 100).toFixed(0)}% (w{it.breakdown.weights.verify})</span>
                  <span>Plan: {(it.breakdown.plan * 100).toFixed(0)}% (w{it.breakdown.weights.plan})</span>
                  <span>Follow-up: {(it.breakdown.followup * 100).toFixed(0)}% (w{it.breakdown.weights.followup})</span>
                  <span>Support: {(it.breakdown.support * 100).toFixed(0)}% (w{it.breakdown.weights.support})</span>
                </div>
                <p className="mt-2 text-[11px] text-zinc-600">Max {fmt(it.max_amount, it.currency)} × {it.score}% performance</p>
              </div>
            ))}
          <p className="mt-4 text-[11px] text-zinc-600">You're never penalised for a member's inactivity — only for actions within your control.</p>
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6">
          <h3 className="font-display text-lg font-bold uppercase tracking-tight flex items-center gap-2"><Banknote className="w-5 h-5 text-[#E53E3E]" /> Ledger & Payouts</h3>
          <div className="mt-4 space-y-2 max-h-80 overflow-y-auto" data-testid="wallet-ledger">
            {(!w.ledger || w.ledger.length === 0) ? <p className="text-sm text-zinc-500">No ledger entries yet.</p> :
              w.ledger.map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-lg border border-zinc-800 px-3 py-2 text-sm">
                  <div><p className="capitalize">{e.type} {e.member_name ? `· ${e.member_name}` : ""}</p><p className="text-xs text-zinc-600">{e.period} · {e.currency}</p></div>
                  <div className="text-right"><p className={e.amount < 0 ? "text-[#E53E3E]" : "text-green-400"}>{fmt(e.amount, e.currency)}</p><Badge className="bg-zinc-800 text-zinc-300 text-[10px]">{e.status}</Badge></div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
